
import { supabase } from "@/integrations/supabase/client";
import { ContextProfile, Lead } from "@/types";
import { Tables } from "@/integrations/supabase/types";

// Create a context profile for a lead
export const createContextProfile = async (contextProfile: Omit<ContextProfile, "id" | "created_at">): Promise<Tables<"context_profiles">> => {
  const { data, error } = await supabase
    .from("context_profiles")
    .insert(contextProfile)
    .select()
    .single();

  if (error) throw error;
  return data;
};

// Get context profile for a lead
export const getContextProfileByLeadId = async (leadId: string): Promise<Tables<"context_profiles"> | null> => {
  const { data, error } = await supabase
    .from("context_profiles")
    .select("*")
    .eq("lead_id", leadId)
    .maybeSingle();

  if (error) throw error;
  return data;
};

// Generate a context profile for a lead using AI
export const generateContextProfile = async (lead: Lead): Promise<ContextProfile> => {
  try {
    console.log(`Generating context profile for lead: ${lead.name}`);
    
    // Call Claude or GPT-4 via edge function to analyze the lead and generate insights
    const { data, error } = await supabase.functions.invoke("generate-context-profile", {
      body: { lead },
    });

    if (error) throw error;
    if (!data.success) throw new Error(data.error || "Failed to generate context profile");

    // Create the context profile in the database
    const contextProfile: Omit<ContextProfile, "id" | "created_at"> = {
      lead_id: lead.id!,
      tone: data.tone,
      summary: data.summary,
      vehicle_interest: data.vehicle_interest,
      next_best_action: data.next_best_action,
      ai_model: data.model_used || "fallback" // Ensure this is never undefined
    };

    return await createContextProfile(contextProfile) as ContextProfile;
  } catch (error) {
    console.error("Error generating context profile:", error);
    // Create a basic profile if AI analysis fails
    const fallbackProfile: Omit<ContextProfile, "id" | "created_at"> = {
      lead_id: lead.id!,
      tone: "professional",
      summary: "No AI analysis available",
      vehicle_interest: "Unknown",
      next_best_action: "Contact for more information",
      ai_model: "fallback"
    };
    return await createContextProfile(fallbackProfile) as ContextProfile;
  }
};
